import { useRef, useState, type KeyboardEvent } from 'react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { EditorKind } from './importModel';

/** Radix Select items cannot have '' as value. */
const EMPTY = '__empty__';

type CellEditorProps = {
  kind: EditorKind;
  value: string;
  countries: string[];
  onCommit: (value: string) => void;
  onCancel: () => void;
};

/**
 * The inline editor of one cell. Text, number and date inputs commit on blur
 * or Enter and cancel on Escape; selects commit on choice.
 */
export function CellEditor(props: CellEditorProps) {
  switch (props.kind) {
    case 'boolean':
      return (
        <ChoiceEditor
          {...props}
          options={[
            { value: 'true', label: 'true' },
            { value: 'false', label: 'false' },
          ]}
        />
      );
    case 'blockType':
      return (
        <ChoiceEditor
          {...props}
          options={[
            { value: '', label: '(empty)' },
            { value: 'TEMPORARY', label: 'TEMPORARY' },
            { value: 'PERMANENTLY', label: 'PERMANENTLY' },
          ]}
        />
      );
    case 'country':
      return (
        <ChoiceEditor
          {...props}
          options={[
            { value: '', label: '(empty)' },
            ...props.countries.map((code) => ({ value: code, label: code })),
          ]}
        />
      );
    case 'date':
      return <DateEditor {...props} />;
    default:
      return <TextEditor {...props} />;
  }
}

/**
 * Enter commits, Escape cancels, blur commits. Only the first of them counts,
 * so the blur that follows an Escape does not commit the draft.
 */
function useDraftHandlers(
  getDraft: () => string,
  onCommit: (value: string) => void,
  onCancel: () => void,
) {
  const done = useRef(false);
  const commit = () => {
    if (!done.current) {
      done.current = true;
      onCommit(getDraft());
    }
  };
  const cancel = () => {
    if (!done.current) {
      done.current = true;
      onCancel();
    }
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      cancel();
    }
  };
  return { onBlur: commit, onKeyDown };
}

function TextEditor({ kind, value, onCommit, onCancel }: CellEditorProps) {
  const [draft, setDraft] = useState(value);
  const handlers = useDraftHandlers(() => draft.trim(), onCommit, onCancel);
  return (
    <Input
      autoFocus
      className="tw:h-8 tw:min-w-28"
      type={kind === 'days' ? 'number' : 'text'}
      min={kind === 'days' ? 1 : undefined}
      max={kind === 'days' ? 365 : undefined}
      step={kind === 'days' ? 1 : undefined}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      {...handlers}
    />
  );
}

/** DD.MM.YYYY as YYYY-MM-DD; any other text is returned as it is. */
function toIsoDate(text: string): string {
  const german = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(text.trim());
  if (german) {
    return `${german[3]}-${german[2].padStart(2, '0')}-${german[1].padStart(2, '0')}`;
  }
  return text.trim();
}

function DateEditor({ value, onCommit, onCancel }: CellEditorProps) {
  const initial = toIsoDate(value);
  const representable = initial === '' || /^\d{4}-\d{2}-\d{2}$/.test(initial);
  const [draft, setDraft] = useState(representable ? initial : '');
  const handlers = useDraftHandlers(() => draft, onCommit, onCancel);
  return (
    <div className="tw:flex tw:flex-col tw:gap-1">
      <Input
        autoFocus
        className="tw:h-8 tw:min-w-36"
        type="date"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        {...handlers}
      />
      {!representable && (
        <span className="tw:text-muted-foreground tw:text-xs">
          File value: {value}
        </span>
      )}
    </div>
  );
}

type Option = { value: string; label: string };

function ChoiceEditor({
  value,
  options,
  onCommit,
  onCancel,
}: CellEditorProps & { options: Option[] }) {
  const current = value.trim();
  const known = options.some(
    (option) => option.value.toLowerCase() === current.toLowerCase(),
  );
  // Keep a value the file has but the list does not, so the user sees it.
  const allOptions: Option[] = known
    ? options
    : [...options, { value: current, label: `${current} (file value)` }];
  const selected =
    allOptions.find(
      (option) => option.value.toLowerCase() === current.toLowerCase(),
    )?.value ?? current;
  return (
    <Select
      defaultOpen
      value={selected === '' ? EMPTY : selected}
      onValueChange={(next) => onCommit(next === EMPTY ? '' : next)}
      onOpenChange={(open) => {
        if (!open) {
          // Runs after onValueChange when an item was chosen; then a no-op.
          onCancel();
        }
      }}
    >
      <SelectTrigger size="sm" className="tw:min-w-28">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {allOptions.map((option) => (
          <SelectItem
            key={option.value || EMPTY}
            value={option.value === '' ? EMPTY : option.value}
          >
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
